import lodash from 'lodash';
import moment from 'moment';
import DateTime from 'common/components/DateTime/DateTime';

const SELECT_INPUT_FIELD_TYPES = ['multi-select', 'single-select'];

export function combineMetadata(studyMetadata = []) {
    return studyMetadata.reduce((acc, studyMetadata) => {
        const existingValue = acc[studyMetadata.descriptorId];
        if (existingValue) {
            acc[studyMetadata.descriptorId] = lodash.concat(existingValue, studyMetadata.value);
        }
        else {
            acc[studyMetadata.descriptorId] = studyMetadata.value;
        }
        return acc;
    }, {});
}

export function normalizeValues(value, inputFieldType) {
    if(lodash.isNil(value)){
        return null;
    }
    const valueFormatMap = {
        'single-value' : value => value.toString(),
        'multi-line' : value => value,
        'date-value' : value => moment(value).toISOString(),
        'date-time-value': value => moment(value).toISOString(),
        'multi-select' : values => values.map(value => value.value),
        'single-select' : value => value.value
    };

    const valueFormatter = valueFormatMap[inputFieldType];
    return valueFormatter(value);
}

export function formatMetadataToAdd(toAddStudyMetadata, studyDescriptors){
    return toAddStudyMetadata.map( studyMetadata => {
        return buildStudyMetadata(lodash.find(studyDescriptors, descriptor => descriptor.descriptorId === studyMetadata.descriptorId), studyMetadata.value);
    });
}

export function formatMetadataToRemove(toRemoveStudyMetadata, initialStudyMetadata){
    return lodash.intersectionWith(initialStudyMetadata, toRemoveStudyMetadata,  (initial, toRemove) => {
        return initial.value === toRemove.value && initial.descriptorId === toRemove.descriptorId;
    });
}

export function getMetadataToUpdate(initialValues, newValues) {
    let addCollection =[], removeCollection =[];

    lodash.map(newValues, (newValue, descriptorId) =>{
        const initialValue = initialValues[descriptorId];
        const metadataWithUpdatedValue = newValue !== initialValue;
        const metadataWithNoValue = lodash.isEmpty(newValue) || lodash.isNil(newValue);
        const isArrayValue = (value) => Array.isArray(value);
        if(lodash.isNil(initialValue)){
            addCollection = [
                ...addCollection, 
                ...addToCollection(newValue, descriptorId, isArrayValue(newValue))
            ];
            return;
        }
        if(metadataWithUpdatedValue && (!lodash.isEmpty(newValue))){
            addCollection = [
                ...addCollection,
                ...addExistingToCollection(initialValue, newValue, descriptorId, isArrayValue(newValue))
            ];
            removeCollection =[
                ...removeCollection,
                ...addExistingToCollection(newValue, initialValue, descriptorId, isArrayValue(initialValue))
            ];
            return;
        }
        if(metadataWithNoValue){
            removeCollection= [
                ...removeCollection, ...addToCollection(initialValue, descriptorId, isArrayValue(initialValue))
            ];
            return;
        }
    });

    return { addCollection , removeCollection };
}
export function formatValue(studyMetadataValues, studyDescriptor){
    const { descriptorId, inputFieldType } = studyDescriptor;
    const value = lodash.get(studyMetadataValues, descriptorId, null);
    let formattedValue = value;
    if(SELECT_INPUT_FIELD_TYPES.includes(inputFieldType) && !lodash.isNil(value)){
        formattedValue = Array.isArray(value) ?
            value.map(value => formatSelectedValue(value))
            :
            formatSelectedValue(value);
    }
    return formattedValue;
}

export function formatViewValue(value) {
    if(Array.isArray(value)) {
        return value.join(', ');
    }
    if(value && (new Date(value) !== 'Invalid Date') && !isNaN(new Date(value))) {
        return <DateTime value={value} format={'DD/MM/YYYY'} applyTimezoneOffset />;
    }
    
    return value;
}

export function formatInputValue(value, inputFieldType) {
    if(Array.isArray(value)) {
        return value.join(', ');
    }
    if (inputFieldType === 'date-value') {
        return <DateTime value={value} format={'DD/MM/YYYY'} applyTimezoneOffset />;
    } else {
        return value;
    }
}

export const formatSelectedValue = (value) => {
    if(!value){
        return null;   
    }
    return {
        label: value,
        value
    };    
};

export function assignDataSourceToMetadata(binderDescriptors, dataSources, studyId, initialBinderMetadataValues){
    return lodash.map(binderDescriptors, descriptor => {
        const dataSource = dataSources[descriptor.dataSourceId];
        const initialValue = initialBinderMetadataValues[descriptor.descriptorId];

        let newDescriptor = populateMetadata(descriptor, dataSource, studyId);
        if(initialValue){
            newDescriptor = assignValue(newDescriptor, initialValue);
        }
        return newDescriptor;
    });
}

function buildStudyMetadata(studyEssential, studyMetadataValue) {
    const STUDY_METADATA_FIELDS = ['type', 'name', 'value', 'dataType', 
        'descriptorId', 'institutionId', 'inputFieldType'];
    const studyMetadata = lodash.pick(studyEssential, STUDY_METADATA_FIELDS);
    
    return lodash.set(studyMetadata, 'value', studyMetadataValue);
}

function addToCollection(newValue, descriptorId, isArrayValues){
    let collection = [];
    if(!isArrayValues) {
        collection.push({descriptorId, value: newValue});
        return collection;
    }
    newValue.forEach(value => collection.push({descriptorId, value: value}));

    return collection;
}

function addExistingToCollection(toCompare, toAdd, descriptorId, isArrayValues){
    let collection = [];
    if(!isArrayValues) {
        collection.push({descriptorId, value: toAdd});
        return collection;
    }
    const valuesAdded = lodash.difference(toAdd, toCompare);
    valuesAdded.forEach(value => collection.push({descriptorId, value: value}));
    return collection;
}

function populateMetadata(descriptor, dataSource, studyId){
    
    return {
        ...descriptor,
        'dataSources': dataSource,
        'studyId': studyId,
        'type': 'binder',
    };
}

function assignValue(descriptor, value){
    return {
        ...descriptor, 'value' : value
    };
}

export function formattedUserOptions(users) {
    return users.map(user => (
        {
            value: user.userId, 
            label: user.displayName, 
            sub: user.sub,
        }
    ));
}
